/**
 * Alan adına göre uygulama modu.
 *
 * Üretimde dört alan adı vardır:
 *   hrtakip.com          → eski (karma) dağıtım, yönetim girişi varsayılan
 *   app.hrtakip.com      → eski (karma) dağıtım, çalışan girişi varsayılan
 *   admin.hrtakip.com    → yalnızca yönetim paneli (dashboard)
 *   employee.hrtakip.com → yalnızca çalışan portalı
 *
 * admin./employee. alan adlarında katı ayırım uygulanır: yanlış taraftaki
 * bir rota istendiğinde kullanıcı doğru alan adına yönlendirilir.
 */

export type AppHostMode = 'admin' | 'employee' | 'legacy'

const ADMIN_HOSTS = ['admin.hrtakip.com']
const EMPLOYEE_HOSTS = ['employee.hrtakip.com', 'app.hrtakip.com']

/** Yerel geliştirmede ?host=admin / ?host=employee ile mod taklit edilebilir. */
function devOverride(): AppHostMode | null {
  if (import.meta.env.PROD) return null
  try {
    const v = new URLSearchParams(window.location.search).get('host')
    if (v === 'admin' || v === 'employee' || v === 'legacy') return v
    const stored = localStorage.getItem('hr-portal.hostMode')
    if (stored === 'admin' || stored === 'employee' || stored === 'legacy') return stored
  } catch {
    /* erişilemiyorsa yok say */
  }
  return null
}

export function getHostMode(hostname = typeof window !== 'undefined' ? window.location.hostname : ''): AppHostMode {
  const override = devOverride()
  if (override) return override
  const h = hostname.toLowerCase()
  if (ADMIN_HOSTS.includes(h)) return 'admin'
  if (EMPLOYEE_HOSTS.includes(h)) return 'employee'
  return 'legacy'
}

/** Katı ayırımın uygulandığı (tek amaçlı) alan adları. */
export function isDedicatedHost(hostname?: string): boolean {
  const h = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase()
  return ADMIN_HOSTS.includes(h) || h === 'employee.hrtakip.com' || (!import.meta.env.PROD && devOverride() !== null)
}

/** Yanlış tarafa düşen kullanıcı için hedef alan adı (yalnızca üretimde kullanılır). */
export function counterpartOrigin(target: 'admin' | 'employee'): string {
  const host = target === 'admin' ? ADMIN_HOSTS[0] : 'employee.hrtakip.com'
  return `https://${host}`
}

/**
 * Çoklu dil (dil seçici) yalnızca bu alan adlarında açıktır.
 * Diğerleri — hrtakip.com, app.hrtakip.com — Türkçe çalışır.
 *
 * Tek imaj tüm alan adlarını sunar; ayrım çalışma zamanında burada yapılır.
 * Yerel geliştirmede VITE_ENABLE_I18N=true ile açılabilir.
 */
const I18N_HOSTS = ['admin.hrtakip.com', 'employee.hrtakip.com']

export function isI18nHost(
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
): boolean {
  if (I18N_HOSTS.includes(hostname.toLowerCase())) return true
  // Yerel geliştirme / önizleme için açık kapı
  if (!import.meta.env.PROD && String(import.meta.env.VITE_ENABLE_I18N) === 'true') return true
  return false
}
